/**
 * Hero Overlay Renderer
 * Renders property data overlays (info bar + badge) onto images using Canvas.
 * Two templates: "Klassisch" (white bar) and "Modern" (dark floating card).
 */

import type { HeroOverlayConfig, AspectRatio } from '@/types';
import { loadGoogleFont } from '@/lib/overlayTemplates';

// ============================================================================
// Icon SVG Paths (from Lucide icons, 24x24 viewBox)
// ============================================================================

const ICON_PATHS: Record<string, string> = {
  // MapPin
  location: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  // Home
  home: 'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8 M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  // Maximize (square for area)
  area: 'M8 3H5a2 2 0 0 0-2 2v3 M21 8V5a2 2 0 0 0-2-2h-3 M3 16v3a2 2 0 0 0 2 2h3 M16 21h3a2 2 0 0 0 2-2v-3',
  // DoorOpen (rooms)
  rooms: 'M13 4h3a2 2 0 0 1 2 2v14 M2 20h3 M13 20h9 M10 12v.01 M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z',
  // Euro
  price: 'M4 10h12 M4 14h12 M17.5 7.5C16.5 5.5 14.5 4 12 4 8.13 4 5 7.13 5 11s3.13 7 7 7c2.5 0 4.5-1.5 5.5-3.5',
};

// ============================================================================
// Color Utilities
// ============================================================================

const getContrastColor = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1a2e' : '#ffffff';
};

// ============================================================================
// Canvas Helpers
// ============================================================================

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
};

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const drawIcon = (
  ctx: CanvasRenderingContext2D,
  pathData: string,
  x: number, y: number,
  size: number,
  color: string
) => {
  ctx.save();
  const scale = size / 24; // Icons are 24x24 viewBox
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.fillStyle = 'none';

  // Parse and draw multiple sub-paths
  const subPaths = pathData.split(/(?=[MmZz])|(?<=Z\s)/g).join('');
  const path = new Path2D(subPaths);
  ctx.stroke(path);
  ctx.restore();
};

// ============================================================================
// Template Renderers
// ============================================================================

/**
 * Template A: "Klassisch"
 * White bar at bottom with 3 columns (icon + text), badge top-left
 */
const renderKlassisch = (
  ctx: CanvasRenderingContext2D,
  config: HeroOverlayConfig,
  w: number, h: number, sf: number
) => {
  // Collect visible data items
  const items: Array<{ icon: string; text: string }> = [];
  if (config.showLocation && config.location) items.push({ icon: 'location', text: config.location });
  if (config.showPropertyType && config.propertyType) items.push({ icon: 'home', text: config.propertyType });
  if (config.showArea && config.area) items.push({ icon: 'area', text: config.area });
  if (config.showRooms && config.rooms) items.push({ icon: 'rooms', text: config.rooms });
  if (config.showPrice && config.price) items.push({ icon: 'price', text: config.price });

  if (items.length > 0) {
    // === Bottom data bar ===
    const barH = Math.round(90 * sf);
    const barY = h - barH;

    // White semi-transparent background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.93)';
    ctx.fillRect(0, barY, w, barH);

    // Thin accent line on top of bar
    ctx.fillStyle = config.accentColor;
    ctx.fillRect(0, barY, w, Math.round(3 * sf));

    // Distribute items evenly
    const colW = w / items.length;
    const iconSz = Math.round(20 * sf);
    const textFontSize = Math.round(20 * sf);
    const centerY = barY + barH / 2;
    const gap = Math.round(10 * sf);

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    items.forEach((item, i) => {
      const colX = colW * i;
      const colCenterX = colX + colW / 2;

      // Measure text width to center icon+text group
      ctx.font = `600 ${textFontSize}px '${config.fontFamily}', sans-serif`;
      const textW = ctx.measureText(item.text).width;
      const groupW = iconSz + gap + textW;
      const startX = colCenterX - groupW / 2;

      // Draw icon
      drawIcon(ctx, ICON_PATHS[item.icon], startX, centerY - iconSz / 2, iconSz, config.accentColor);

      // Draw text
      ctx.fillStyle = '#1a1a2e';
      ctx.font = `600 ${textFontSize}px '${config.fontFamily}', sans-serif`;
      ctx.fillText(item.text, startX + iconSz + gap, centerY);

      // Draw separator line (between columns, not after last)
      if (i < items.length - 1) {
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)';
        ctx.lineWidth = Math.round(1 * sf);
        ctx.beginPath();
        ctx.moveTo(colX + colW, barY + Math.round(20 * sf));
        ctx.lineTo(colX + colW, barY + barH - Math.round(20 * sf));
        ctx.stroke();
      }
    });
  }

  // === Top-left badge ===
  if (config.badgeEnabled && config.badgeText) {
    const bs = config.badgeScale ?? 1.0;
    const badgeFontSize = Math.round(17 * sf * bs);
    const badgePadH = Math.round(18 * sf * bs);
    const badgePadV = Math.round(10 * sf * bs);
    const badgeMargin = Math.round(28 * sf);

    ctx.font = `700 ${badgeFontSize}px '${config.fontFamily}', sans-serif`;
    const textWidth = ctx.measureText(config.badgeText).width;

    const badgeW = textWidth + badgePadH * 2;
    const badgeH = badgeFontSize + badgePadV * 2;

    // Badge background
    ctx.fillStyle = config.accentColor;
    ctx.fillRect(badgeMargin, badgeMargin, badgeW, badgeH);

    // Badge text
    ctx.fillStyle = getContrastColor(config.accentColor);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillText(config.badgeText, badgeMargin + badgePadH, badgeMargin + badgeH / 2);
  }
};

/**
 * Template B: "Modern"
 * Semi-transparent dark card bottom-left, pill badge top-left
 */
const renderModern = (
  ctx: CanvasRenderingContext2D,
  config: HeroOverlayConfig,
  _w: number, h: number, sf: number
) => {
  // Collect visible data items
  const items: string[] = [];
  if (config.showLocation && config.location) items.push(config.location);
  if (config.showPropertyType && config.propertyType) items.push(config.propertyType);
  if (config.showArea && config.area) items.push(config.area);
  if (config.showRooms && config.rooms) items.push(config.rooms);
  if (config.showPrice && config.price) items.push(config.price);

  if (items.length > 0) {
    // === Bottom-left floating card ===
    const lineHeight = Math.round(36 * sf);
    const cardPadH = Math.round(28 * sf);
    const cardPadV = Math.round(22 * sf);
    const cardMargin = Math.round(36 * sf);
    const cardRadius = Math.round(14 * sf);
    const accentW = Math.round(4 * sf);
    const textFontSize = Math.round(19 * sf);

    const cardH = cardPadV * 2 + items.length * lineHeight;
    // Measure widest text
    ctx.font = `600 ${textFontSize}px '${config.fontFamily}', sans-serif`;
    const maxTextW = Math.max(...items.map(t => ctx.measureText(t).width));
    const cardW = accentW + cardPadH * 2 + maxTextW + Math.round(20 * sf);

    const cardX = cardMargin;
    const cardY = h - cardH - cardMargin;

    // Dark semi-transparent background
    ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
    roundRect(ctx, cardX, cardY, cardW, cardH, cardRadius);
    ctx.fill();

    // Left accent stripe
    const stripeR = Math.round(3 * sf);
    ctx.fillStyle = config.accentColor;
    roundRect(ctx, cardX, cardY, accentW + stripeR, cardH, cardRadius);
    ctx.fill();
    // Clip right side of stripe to not overflow
    ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
    ctx.fillRect(cardX + accentW, cardY + stripeR, stripeR, cardH - stripeR * 2);

    // Simpler: just draw a rect stripe
    ctx.fillStyle = config.accentColor;
    ctx.fillRect(cardX + cardRadius, cardY + Math.round(12 * sf), accentW, cardH - Math.round(24 * sf));

    // Text lines
    ctx.fillStyle = '#ffffff';
    ctx.font = `600 ${textFontSize}px '${config.fontFamily}', sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    const textX = cardX + accentW + cardPadH;
    let textY = cardY + cardPadV;

    items.forEach((text) => {
      ctx.fillText(text, textX, textY);
      textY += lineHeight;
    });
  }

  // === Pill badge top-left ===
  if (config.badgeEnabled && config.badgeText) {
    const badgeFontSize = Math.round(15 * sf);
    const pillPadH = Math.round(20 * sf);
    const pillPadV = Math.round(9 * sf);
    const pillMargin = Math.round(28 * sf);
    const pillRadius = Math.round(22 * sf);

    ctx.font = `700 ${badgeFontSize}px '${config.fontFamily}', sans-serif`;
    const tw = ctx.measureText(config.badgeText).width;

    const pillW = tw + pillPadH * 2;
    const pillH = badgeFontSize + pillPadV * 2;

    ctx.fillStyle = config.accentColor;
    roundRect(ctx, pillMargin, pillMargin, pillW, pillH, pillRadius);
    ctx.fill();

    ctx.fillStyle = getContrastColor(config.accentColor);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillText(config.badgeText, pillMargin + pillPadH, pillMargin + pillH / 2);
  }
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Draw hero overlay onto an image and return data URL.
 * Used for full-resolution export/save.
 */
export const drawHeroOverlay = async (
  baseImageUrl: string,
  config: HeroOverlayConfig,
  _aspectRatio: AspectRatio
): Promise<string> => {
  // Load font and ensure it's ready for canvas
  await loadGoogleFont(config.fontFamily);
  await document.fonts.ready;

  // Load base image
  const img = await loadImage(baseImageUrl);

  // Create canvas matching image size
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d')!;

  // Draw base image
  ctx.drawImage(img, 0, 0);

  // Scale factor relative to 2400px reference width, multiplied by user scale
  const scale = config.overlayScale ?? 1.0;
  const sf = (canvas.width / 2400) * scale;

  // Render selected template
  if (config.templateId === 'modern') {
    renderModern(ctx, config, canvas.width, canvas.height, sf);
  } else {
    renderKlassisch(ctx, config, canvas.width, canvas.height, sf);
  }

  return canvas.toDataURL('image/png');
};

/**
 * Render hero overlay preview onto a provided canvas element.
 * Used for live preview in the editor (scaled down).
 */
export const renderHeroPreviewToCanvas = async (
  baseImageUrl: string,
  config: HeroOverlayConfig,
  _aspectRatio: AspectRatio,
  targetCanvas: HTMLCanvasElement
): Promise<void> => {
  // Load font and ensure it's ready for canvas
  await loadGoogleFont(config.fontFamily);
  await document.fonts.ready;

  // Load base image
  const img = await loadImage(baseImageUrl);

  // Set canvas dimensions to match display size * devicePixelRatio for crisp rendering
  const displayWidth = targetCanvas.clientWidth;
  const displayHeight = targetCanvas.clientHeight || (displayWidth * img.naturalHeight / img.naturalWidth);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  targetCanvas.width = displayWidth * dpr;
  targetCanvas.height = displayHeight * dpr;

  const ctx = targetCanvas.getContext('2d')!;
  ctx.scale(dpr, dpr);

  // Draw base image scaled to display size
  ctx.drawImage(img, 0, 0, displayWidth, displayHeight);

  // Scale factor for the preview (relative to display width), multiplied by user scale
  const scale = config.overlayScale ?? 1.0;
  const sf = (displayWidth / 2400) * scale;

  // Render selected template
  if (config.templateId === 'modern') {
    renderModern(ctx, config, displayWidth, displayHeight, sf);
  } else {
    renderKlassisch(ctx, config, displayWidth, displayHeight, sf);
  }
};

/**
 * Default hero overlay config, pre-populated from project data.
 */
export const getDefaultHeroConfig = (project: {
  address?: string;
  propertyType?: string | null;
  propertyAreaSqm?: number | null;
  propertyRooms?: number | null;
  propertyPrice?: string | null;
  propertyStatus?: string | null;
}): HeroOverlayConfig => {
  // Extract city from address (take last comma-separated part, or full address)
  const addressParts = (project.address || '').split(',').map(s => s.trim());
  const city = addressParts.length > 1 ? addressParts[addressParts.length - 1] : addressParts[0] || '';

  return {
    templateId: 'klassisch',
    badgeText: project.propertyStatus || 'NEU IM VERKAUF',
    badgeEnabled: true,
    location: city,
    propertyType: project.propertyType || '',
    area: project.propertyAreaSqm ? `${project.propertyAreaSqm} m²` : '',
    rooms: project.propertyRooms ? `${project.propertyRooms} Zimmer` : '',
    price: project.propertyPrice || '',
    showLocation: !!city,
    showPropertyType: !!project.propertyType,
    showArea: !!project.propertyAreaSqm,
    showRooms: !!project.propertyRooms,
    showPrice: false,
    accentColor: '#1a365d',
    fontFamily: 'Montserrat',
    overlayScale: 2.0,
    badgeScale: 1.0,
  };
};
